import { Injectable } from '@angular/core';
import { concat, Observable, of, zip } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';

import { CheckApiRepository } from '../repositories/check-api.repository';
import { CheckCacheRepository } from '../repositories/check-cache.repository';
import { ClientApiRepository } from '../repositories/client-api.repository';
import { ClientCacheRepository } from '../repositories/client-cache.repository';
import {
    ClientInterface,
    ClientNotificationInterface,
    ClientRelation,
    ClientType,
    ContractInterface,
    DocumentType,
    DocumentTypeId,
    FullClientInterface,
    IdInterface,
    ValidationClient,
} from '../data';
import { DocumentApiRepository } from '../repositories/document-api.repository';
import { FullClientApiRepository } from '../repositories/full-client-api.repository';
import { FullClientCacheRepository } from '../repositories/full-client-cache.repository';
import { Transaction } from '../transaction/transaction';
import { ValidationRepository } from '../repositories/validation.repository';

const clientFill = (contract: ContractInterface) =>
    switchMap(
        (transaction: Transaction<FullClientInterface>): Observable<Transaction<FullClientInterface>> =>
            transaction.map(
                (client) => {

                    if (client.encryptedCode) {
                        client.emailConfirm = client.email;
                        client.phoneConfirm = client.phone;

                        if (client.type === ClientType.MINOR || client.type === ClientType.DISABLED) {
                            //client.professional.doAnyActivity = client.professional.employmentSituation? true: false;
                            if (!client.professional.doAnyActivity && !client.professional.position) {
                                client.professional.position = client.type === ClientType.MINOR ? '0302' : '0100';
                            }

                        }


                        contract.holders.map((holder) => {
                            client.isSoleHolder =
                                contract.holders.length +
                                    contract.legalGuardians.filter(
                                        (legalGuardian) => legalGuardian.legalGuardianRelation === ClientRelation.HOLDER
                                    ).length ===
                                1;

                            if (holder.id.value === client.id.value) {
                                client.relation = ClientRelation.HOLDER;
                                client.legalGuardianType = holder.legalGuardianType;
                                client.legalGuardianRelation = holder.legalGuardianRelation;
                            } else {
                                holder.legalGuardians.map((legalGuardian) => {

                                    if (legalGuardian.id.value === client.id.value) {
                                        client.relation = legalGuardian.relation;
                                        client.legalGuardianType = legalGuardian.legalGuardianType;
                                        client.legalGuardianRelation = legalGuardian.legalGuardianRelation;
                                    }
                                });
                            }
                        });

                        contract.authorized.map((authorized) => {
                            if (authorized.id.value === client.id.value) {
                                client.relation = ClientRelation.AUTHORIZED;
                                //client.professional.doAnyActivity = false;
                            }
                        });
                    }

                    return of(Transaction.success(client));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

const clientDocuments = (documentApiRepository: DocumentApiRepository, contract: ContractInterface) =>
    switchMap(
        (transaction: Transaction<FullClientInterface>): Observable<Transaction<FullClientInterface>> =>
            transaction.map(
                (client) => {
                    if (client.encryptedCode) {
                        return documentApiRepository.loadClient(client.encryptedCode, contract.encryptedCode).pipe(
                            map((transactionDocument) =>
                                transactionDocument.map(
                                    (documents) => {
                                        client.documents = documents.filter((it) => String(it.typeId) !== DocumentTypeId.ORIGIN_FUNDS && String(it.typeId) !== DocumentTypeId.INVERSIS);
                                        client.documentsInversis = documents.filter((it) => String(it.typeId) === DocumentTypeId.INVERSIS);
                                        client.documentsOriginFunds = documents.filter((it) => String(it.typeId) === DocumentTypeId.ORIGIN_FUNDS);
                                        return Transaction.success(client);
                                    },
                                    (throwable) => Transaction.error(throwable.message)
                                )
                            )
                        );
                    }

                    return of(Transaction.success(client));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

const clientValidation = (validationRepository: ValidationRepository) =>
    switchMap(
        (transaction: Transaction<FullClientInterface>): Observable<Transaction<FullClientInterface>> =>
            transaction.map(
                (client) => {
                    if (client.id) {
                        return validationRepository.load(client.id).pipe(
                            map((transactionValidation) =>
                                transactionValidation.map(
                                    (validation) => {
                                        client.validation = validation;
                                        return Transaction.success(client);
                                    },
                                    (_) => Transaction.success(client)
                                )
                            )
                        );
                    }

                    return of(Transaction.success(client));
                },
                (throwable) => of(Transaction.error(throwable.message))
            )
    );

@Injectable()
export class ClientProvider {
    constructor(
        private checkApiRepository: CheckApiRepository,
        private checkCacheRepository: CheckCacheRepository,
        private clientApiRepository: ClientApiRepository,
        private clientCacheRepository: ClientCacheRepository,
        private documentApiRepository: DocumentApiRepository,
        private fullClientApiRepository: FullClientApiRepository,
        private fullClientCacheRepository: FullClientCacheRepository,
        private validationRepository: ValidationRepository
    ) {}

    existClient(client: ClientInterface, catpcha: string): Observable<Transaction<boolean>> {
        return this.checkApiRepository.checkClient(client, catpcha);
    }

    existByEmail(email: string, clientCode?: IdInterface): Observable<Transaction<boolean>> {
        const cacheId = this.checkCacheRepository.getKey(email + (clientCode ? clientCode.value : ''));

        return concat(
            this.checkCacheRepository.load(cacheId),
            this.checkApiRepository.checkEmail(email, clientCode).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.checkCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    existByPhone(phone: string, clientCode?: IdInterface): Observable<Transaction<boolean>> {
        const cacheId = this.checkCacheRepository.getKey(phone + (clientCode ? clientCode.value : ''));

        return concat(
            this.checkCacheRepository.load(cacheId),
            this.checkApiRepository.checkPhone(phone, clientCode).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.checkCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    find(clientCode: IdInterface, contract: ContractInterface): Observable<Transaction<FullClientInterface>> {
        const cacheId = this.fullClientCacheRepository.getKey(clientCode);

        return concat(
            this.fullClientCacheRepository.load(cacheId),
            this.fullClientApiRepository.load(clientCode, contract.encryptedCode).pipe(
                clientFill(contract),
                clientDocuments(this.documentApiRepository, contract),
                tap((transaction: Transaction<FullClientInterface>) => {
                    if (transaction.isSuccess) {
                        this.fullClientCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first(), clientValidation(this.validationRepository));
    }

    findByDocument(docType: DocumentType, docId: string): Observable<Transaction<ClientInterface>> {
        const cacheId = this.clientCacheRepository.getKey(`${docType}${docId}`);

        return concat(
            this.clientCacheRepository.load(cacheId),
            this.clientApiRepository.load(docType, docId).pipe(
                tap((transaction) => {
                    if (transaction.isSuccess) {
                        this.clientCacheRepository.save(cacheId, transaction.data).subscribe();
                    }
                })
            )
        ).pipe(first());
    }

    reject(contract: ContractInterface, client: ClientInterface, message: string): Observable<Transaction<void>> {
        return this.clientApiRepository.reject(contract, client, message);
    }

    revise(contract: ContractInterface, client: ClientInterface, message: string): Observable<Transaction<void>> {
        return this.clientApiRepository.revise(contract, client, message);
    }

    update(client: FullClientInterface): Observable<Transaction<void>> {
        return new Observable((observer) => {
            const cacheId = this.fullClientCacheRepository.getKey(client.encryptedCode);
            const cacheIdDocId = this.clientCacheRepository.getKey(client.docId);

            this.fullClientApiRepository
                .update(client)
                .pipe(
                    tap((transaction) => {
                        if (transaction.isSuccess) {
                            this.fullClientCacheRepository.remove(cacheId).subscribe();
                            this.clientCacheRepository.remove(cacheIdDocId).subscribe();
                            this.checkCacheRepository.clear().subscribe();
                        }
                    })
                )
                .subscribe((transaction) => {
                    if (transaction.isSuccess) {
                        const uploadDocuments$: Observable<Transaction<void>>[] = this.getUploadDocuments(client);

                        if (uploadDocuments$.length > 0) {
                            concat(...uploadDocuments$)
                                .pipe(
                                    tap((transactionUpload: Transaction<void>) => {
                                        if (!transactionUpload.isSuccess) {
                                            throw Error(transactionUpload.error);
                                        }
                                    })
                                )
                                .subscribe({
                                    error: (error) => {
                                        observer.next(Transaction.error(error.message));
                                        observer.complete();
                                    },
                                    complete: () => {
                                        observer.next(Transaction.success());
                                        observer.complete();
                                    },
                                });
                        } else {
                            observer.next(Transaction.success());
                            observer.complete();
                        }
                    } else {
                        observer.next(Transaction.error(transaction.error));
                        observer.complete();
                    }
                });
        });
    }

    protected getUploadDocuments(client: FullClientInterface): Observable<Transaction<void>>[] {
        const observables$: Observable<Transaction<void>>[] = [];

        [].concat(client.documents, client.documentsInversis, client.documentsOriginFunds).map((document) => {
            if (document.id && document.remove) {
                observables$.push(this.documentApiRepository.remove(document));
            }

            if (document.file instanceof File || (document.file as Blob) instanceof Blob) {
                observables$.push(this.documentApiRepository.upload(client.encryptedCode, document, false));
            }
        });

        return observables$;
    }

    validate(client: FullClientInterface): Observable<Transaction<void>> {
        return new Observable((observer) => {
            let error: string;

            const validateDocuments$: Observable<Transaction<void>>[] = [];

            client.documents.map((document) => {
                if (document.id) {
                    validateDocuments$.push(this.documentApiRepository.validate(client.encryptedCode, document, false));
                }
            });

            if (validateDocuments$.length > 0) {
                zip(...validateDocuments$)
                    .pipe(
                        tap((transactions: Transaction<void>[]) => {
                            transactions.map((transaction) => {
                                if (!transaction.isSuccess) {
                                    error = transaction.error;
                                }
                            });
                        })
                    )
                    .subscribe({
                        complete: () => {
                            if (error) {
                                observer.next(Transaction.error(error));
                                observer.complete();
                            } else {
                                this.validateClient(client).subscribe((transaction) => {
                                    observer.next(transaction);
                                    observer.complete();
                                });
                            }
                        },
                    });
            } else {
                this.validateClient(client).subscribe((transaction) => {
                    observer.next(transaction);
                    observer.complete();
                });
            }
        });
    }

    private validateClient(client: FullClientInterface): Observable<Transaction<void>> {
        return new Observable((observer) => {
            const cacheId = this.fullClientCacheRepository.getKey(client.encryptedCode);
            const cacheIdDocId = this.clientCacheRepository.getKey(client.docId);

            this.clientApiRepository
                .validate(client)
                .pipe(
                    tap((transaction) => {
                        if (transaction.isSuccess) {
                            this.fullClientCacheRepository.remove(cacheId).subscribe();
                            this.clientCacheRepository.remove(cacheIdDocId).subscribe();
                        }
                    })
                )
                .subscribe((transaction) => {
                    observer.next(transaction);
                    observer.complete();
                });
        });
    }

    validateData(client: FullClientInterface, validations: ValidationClient[]): Observable<Transaction<void>> {
        return new Observable((observer) => {
            const cacheId = this.fullClientCacheRepository.getKey(client.encryptedCode);
            const cacheIdDocId = this.clientCacheRepository.getKey(client.docId);

            this.clientApiRepository
                .validateData(client, validations)
                .pipe(
                    tap((transaction) => {
                        if (transaction.isSuccess) {
                            this.fullClientCacheRepository.remove(cacheId).subscribe();
                            this.clientCacheRepository.remove(cacheIdDocId).subscribe();
                        }
                    })
                )
                .subscribe((transaction) => {
                    observer.next(transaction);
                    observer.complete();
                });
        });
    }

    validateNotifications(): Observable<Transaction<ClientNotificationInterface[]>> {
        return this.clientApiRepository.validateNotifications();
    }
}
